/** WebSocket transport for the reporting agent, behind an interface so the store
 *  depends on the abstraction (and tests inject a fake). Owns socket lifecycle
 *  and frame (de)serialization only — no UI state.
 *
 *  Auth is the app **session cookie**: the socket connects to the API origin
 *  (`API_BASE_URL`) — the same host the REST + dashboard-HTML calls use — so the
 *  browser attaches the cookie to the handshake (same-site, sent with
 *  credentials); the client sends no token (see `apps/reporting-agent`
 *  `realtime/web.py`). A `4401` close means the session is invalid → the store
 *  routes the user to re-auth. Protocol: `realtime/protocol.py`. */

import { API_BASE_URL } from "@/config";

const WS_AUTH_FAILED = 4401;
/** The service refused this chat and said why in an `error` frame just before
 *  closing: the person's role, or a teammate that may not run for them now
 *  (`realtime/web.py`, NG-3619). */
const WS_REFUSED = 4403;

/** Callbacks the store registers; each maps a server frame to a store action. */
export interface ReportingAgentSocketCallbacks {
  onReady: (sessionId: string) => void;
  onDelta: (text: string) => void;
  onAssistantMessage: (text: string) => void;
  onToolStarted: () => void;
  onInputRequest: (toolCallId: string, prompt: string, options: string[] | null) => void;
  onApprovalRequest: (
    toolCallId: string,
    summary: string,
    body: string | null,
    risk: string | null,
  ) => void;
  onDashboardReady: (artifactId: string) => void;
  onTurnComplete: () => void;
  /** A turn/connection `error` frame (post-connect). */
  onTurnError: () => void;
  /** The handshake was rejected as unauthenticated (`4401`). */
  onAuthFailed: () => void;
  /** The service refused the chat (`4403`), with the code of the `error` frame it
   *  sent first (`teammate_turned_off`, `stale_version`, …), or `null` if none. */
  onRefused: (code: string | null) => void;
  /** The socket closed or errored unexpectedly (not 4401, which is `onAuthFailed`,
   *  nor 4403, which is `onRefused`). */
  onError: () => void;
}

export interface IReportingAgentSocket {
  /** Open a connection for the workspace. Implementations MUST tear down any
   *  prior connection first, so a caller can redial without a separate
   *  `disconnect()` — the store's connect-retry relies on this (NG-1269). */
  connect(workspaceSlug: string, callbacks: ReportingAgentSocketCallbacks): void;
  sendUserMessage(text: string): void;
  sendResume(sessionId: string): void;
  cancel(): void;
  sendInputResponse(toolCallId: string, text: string): void;
  sendApprovalResponse(toolCallId: string, approved: boolean, note?: string): void;
  disconnect(): void;
}

interface ServerFrame {
  type: string;
  session_id?: string;
  text?: string;
  tool_call_id?: string;
  name?: string;
  prompt?: string;
  options?: string[] | null;
  summary?: string;
  body?: string | null;
  risk?: string | null;
  artifact_id?: string;
  code?: string | null;
}

/** The reporting-agent WS lives on the **API origin** (the ALB at `API_BASE_URL`)
 *  — the same host the REST + dashboard-HTML calls use — not the app/CloudFront
 *  origin that serves the SPA. Derive `ws(s)://<api-host>/reporting-agent/ws/<slug>`
 *  from `API_BASE_URL` so the handshake reaches the service and carries the
 *  session cookie (same-site, sent with credentials, like the REST calls).
 *
 *  `API_BASE_URL` is empty in local dev, where the API is same-origin behind
 *  nginx — fall back to the page origin there. */
function socketUrl(workspaceSlug: string): string {
  const slug = encodeURIComponent(workspaceSlug);
  const origin = API_BASE_URL ? new URL(API_BASE_URL) : window.location;
  const scheme = origin.protocol === "https:" ? "wss" : "ws";
  return `${scheme}://${origin.host}/reporting-agent/ws/${slug}`;
}

export class ReportingAgentSocket implements IReportingAgentSocket {
  private ws: WebSocket | null = null;
  private callbacks: ReportingAgentSocketCallbacks | null = null;
  /** The code of the last `error` frame on this socket: a `4403` close names its
   *  reason in the frame just before it. */
  private lastErrorCode: string | null = null;

  connect(workspaceSlug: string, callbacks: ReportingAgentSocketCallbacks): void {
    // Tear down any prior connection first (StrictMode double-mount / reconnect)
    // so we never leak a socket.
    this.disconnect();
    this.callbacks = callbacks;
    this.lastErrorCode = null;

    const socket = new WebSocket(socketUrl(workspaceSlug));
    this.ws = socket;

    socket.onmessage = (event: MessageEvent) => {
      // Ignore events from a socket that has since been replaced.
      if (this.ws !== socket) return;
      let frame: ServerFrame;
      try {
        frame = JSON.parse(event.data as string) as ServerFrame;
      } catch (error) {
        console.error("[reporting-agent] failed to parse WS frame", error, event.data);
        this.callbacks?.onTurnError();
        return;
      }
      this.dispatch(frame);
    };

    socket.onerror = () => {
      if (this.ws !== socket) return;
      this.callbacks?.onError();
    };

    socket.onclose = (event: CloseEvent) => {
      // Ignore closes from a replaced socket or after an intentional disconnect.
      if (this.ws !== socket || !this.callbacks) return;
      if (event.code === WS_AUTH_FAILED) {
        this.callbacks.onAuthFailed();
      } else if (event.code === WS_REFUSED) {
        this.callbacks.onRefused(this.lastErrorCode);
      } else if (event.wasClean) {
        // A clean, server-initiated close ends the session normally.
        this.callbacks.onError();
      } else {
        this.callbacks.onError();
      }
    };
  }

  private dispatch(frame: ServerFrame): void {
    const cb = this.callbacks;
    if (!cb) return;
    switch (frame.type) {
      case "ready":
        cb.onReady(frame.session_id ?? "");
        break;
      case "assistant_delta":
        cb.onDelta(frame.text ?? "");
        break;
      case "assistant_message":
        cb.onAssistantMessage(frame.text ?? "");
        break;
      case "tool_started":
        cb.onToolStarted();
        break;
      case "input_request":
        cb.onInputRequest(frame.tool_call_id ?? "", frame.prompt ?? "", frame.options ?? null);
        break;
      case "approval_request":
        cb.onApprovalRequest(
          frame.tool_call_id ?? "",
          frame.summary ?? "",
          frame.body ?? null,
          frame.risk ?? null,
        );
        break;
      case "dashboard_ready":
        cb.onDashboardReady(frame.artifact_id ?? "");
        break;
      case "turn_complete":
        cb.onTurnComplete();
        break;
      case "error":
        this.lastErrorCode = frame.code ?? null;
        cb.onTurnError();
        break;
      // tool_progress / tool_result: acknowledged, no UI action yet.
      case "tool_progress":
      case "tool_result":
        break;
      default:
        // Unknown frame — ignore for forward-compatibility.
        break;
    }
  }

  private send(payload: Record<string, unknown>): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(payload));
    } else {
      console.warn("[reporting-agent] dropped frame; socket not open", payload.type);
      this.callbacks?.onTurnError();
    }
  }

  sendUserMessage(text: string): void {
    this.send({ type: "user_message", text });
  }

  sendResume(sessionId: string): void {
    this.send({ type: "resume", session_id: sessionId });
  }

  cancel(): void {
    this.send({ type: "cancel" });
  }

  sendInputResponse(toolCallId: string, text: string): void {
    this.send({ type: "input_response", tool_call_id: toolCallId, text });
  }

  sendApprovalResponse(toolCallId: string, approved: boolean, note?: string): void {
    this.send({
      type: "approval_response",
      tool_call_id: toolCallId,
      approved,
      note: note ?? null,
    });
  }

  disconnect(): void {
    if (this.ws) {
      // Detach handlers before closing so a queued onclose can't fire into a
      // subsequent session.
      this.ws.onmessage = null;
      this.ws.onerror = null;
      this.ws.onclose = null;
      this.ws.close();
    }
    this.ws = null;
    this.callbacks = null;
  }
}
